// Documents section — quotes, contracts, warranties and other supporting
// files the retailer has shared with the customer.
//
// Source-of-truth references (READ-ONLY):
//   - customer-standalone.html:811-820   "page-documents" panel
//   - customer-live.js:798-813            renderDocuments
//
// 2026-05-20 visual upgrade — replaced the previous list layout with the
// source-of-truth grid (responsive auto-fill tiles) and ported the
// folded-corner "document" icon (pure-CSS ::before fold). Section now
// always renders so the empty state can guide the customer through the
// case where no documents are shared yet.
//
// Documents share the same `DesignFile` shape as designs (see types.ts §134)
// — `fileUrl`, `filename`, `notes`, `uploadedAt`, `fileSize`, `fileType`.
//
// No fetches — file URLs come from the server in the project payload.

import type {
  CustomerProject,
  CustomerSection,
  DesignFile,
  SectionCleanup,
  SectionContext,
} from "../types";

import "./documents.css";

// ── Helpers ──────────────────────────────────────────────────────────────────

const DATE_FMT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function fmtDate(raw: string): string {
  const t = Date.parse(raw);
  if (!Number.isFinite(t)) return "—";
  return DATE_FMT.format(new Date(t));
}

function fmtSize(bytes: number | null): string {
  if (bytes == null || !Number.isFinite(bytes) || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function extOf(file: DesignFile): string {
  if (file.fileType && file.fileType.length > 0) {
    // Some servers return MIME (`application/pdf`); pull the trailing token.
    const mime = file.fileType.toLowerCase();
    const slash = mime.lastIndexOf("/");
    const tail = slash >= 0 ? mime.slice(slash + 1) : mime;
    if (tail && tail.length <= 5) return tail;
  }
  const dot = file.filename.lastIndexOf(".");
  if (dot < 0 || dot === file.filename.length - 1) return "";
  return file.filename.slice(dot + 1).toLowerCase();
}

function badgeLabel(file: DesignFile): string {
  const ext = extOf(file);
  return (ext === "" ? "DOC" : ext).toUpperCase();
}

// ── Section ──────────────────────────────────────────────────────────────────

const documentsSection: CustomerSection = {
  id: "documents",

  shouldRender(_project: CustomerProject): boolean {
    // Always render — empty state guides the customer when the retailer
    // hasn't shared anything yet (per spec 2026-05-20).
    return true;
  },

  mount(container: HTMLElement, ctx: SectionContext): SectionCleanup {
    const docs: DesignFile[] = ctx.project.documents
      .slice()
      .sort((a, b) => {
        const ta = Date.parse(a.uploadedAt) || 0;
        const tb = Date.parse(b.uploadedAt) || 0;
        return tb - ta;
      });

    const card = document.createElement("section");
    card.className = "cust-card doc-card";
    card.setAttribute("aria-labelledby", "doc-title");

    card.innerHTML = `
      <header class="doc-head">
        <h2 id="doc-title" class="cust-card-title">Documents</h2>
        <p class="doc-subtitle">Quotes, contracts, warranties and any other documents your retailer has shared with you.</p>
      </header>
      <div class="doc-grid" data-role="grid">
        ${
          docs.length === 0
            ? `<div class="doc-empty">
                <div class="doc-empty-title">No documents yet</div>
                <div class="doc-empty-body">Receipts, plans and contracts your retailer shares with you will appear here.</div>
              </div>`
            : docs.map(renderTile).join("")
        }
      </div>
    `;

    container.appendChild(card);

    function renderTile(doc: DesignFile): string {
      const ext = extOf(doc) || "doc";
      const badge = badgeLabel(doc);
      const sizeText = fmtSize(doc.fileSize);
      const dateText = fmtDate(doc.uploadedAt);
      const meta = [badge, sizeText, dateText].filter(Boolean).map(esc).join(" · ");
      return `
        <a class="doc-tile"
           href="${esc(doc.fileUrl)}"
           target="_blank"
           rel="noopener noreferrer"
           download="${esc(doc.filename)}"
           title="${esc(doc.filename)}">
          <div class="doc-icon" data-ext="${esc(ext)}">${esc(badge)}</div>
          <div class="doc-tile-name">${esc(doc.filename || "Document")}</div>
          <div class="doc-tile-meta">${meta}</div>
          ${
            doc.notes && doc.notes.trim() !== ""
              ? `<div class="doc-tile-note">${esc(doc.notes.trim())}</div>`
              : ""
          }
        </a>
      `;
    }

    // No listeners attached — anchor tags handle their own click.
    return () => {
      if (card.parentNode === container) container.removeChild(card);
    };
  },
};

export default documentsSection;
